// Opendoor Carousel
//
// Allows navigation via scroll, touch, and next/previous buttons.
//
// Items can be lazily loaded. The carousel simply "notifies" the items to tell
// them to load, and expects the item itself to handle loading. The carousel
// sets `scope().lazyLoad` on the od-carousel-item elements to notify them to
// load.
//
// To use, create the following hierarchy:
// od-carousel                (defines next/prev functions on the scope)
//   od-carousel-container    (necessary to handle scroll events)
//     od-carousel-item       (any number of these, ng-repeat, etc all work)
//
// The carousel only supports direct children in the hierarchy for now, for
// performance and to allow nesting carousels.
//
// To add next/previous buttons, put them anywhere within od-carousel. Style
// od-carousel-container to take up the space you want.
import angular from 'angular';
import { debounce, filter, findIndex, last, reduce, throttle } from 'lodash';

import _css from './od-carousel.less';

module.exports = angular.module('common.directives.od-carousel', [])
.directive('odCarousel', ($window, $timeout, $mediaQuery) => {
  class OdCarousel {
    constructor($element, $scope) {
      this.$element = $element;
      this.$scope = $scope;

      // Current slide position, initially 0. Can be non-integer to represent
      // being "between" slides.
      this.currentPosition = 0;

      // Always an integer
      this.currentIndex = 0;

      // Target slide position, null unless mid-animation after prev/next
      // buttons are clicked. Can be non-integer to represent being "between"
      // slides. We need this so that if the user clicks "next" three times
      // very quickly, we can keep updating the target position.
      this.targetPosition = null;

      // Array of carouselItem objects. Must be rebuilt whenever new
      // od-carousel-item elements are added to the DOM.
      //
      // Each object should have the following properties:
      //   angularEl: Angular element wrapping the DOM od-carousel-item element
      //   width: Width of the item, computed from the DOM
      //   offsetLeft: Total distance (width) from left of all carousel items
      this.carouselItems = [];

      // Reference to the container, to get/set scrollLeft setting.
      this.odCarouselContainer = null;

      var windowEl = angular.element($window);
      var lastWindowWidth = $window.innerWidth;
      var onWindowResize = () => {
        var windowWidth = $window.innerWidth;
        if (windowWidth === lastWindowWidth) {
          return;
        }
        lastWindowWidth = windowWidth;
        this.debouncedRebuild();
      };

      windowEl.on('resize', onWindowResize);
      $scope.$on('$destroy', function() {
        windowEl.off('resize', onWindowResize);
      });

      this.debouncedRebuild = debounce(this._rebuild.bind(this),
                                         100, { leading: true });

      this.throttledLoadImages = throttle(this._loadImages.bind(this), 100);
    }

    // Search for all od-carousel-item elements and rebuild a representation of
    // the carousel.
    //
    // Private - call `debouncedRebuild` instead to ensure good performance.
    _rebuild() {
      var el = this.$element[0];
      var containerEl =
        this.getChildrenByElementOrAttribute(el, 'od-carousel-container')[0];
      var itemElements =
        this.getChildrenByElementOrAttribute(containerEl, 'od-carousel-item');

      // Use reduce so that we can set `offsetLeft` based on the previous
      // element. Return an array of objects, one per carousel-item
      this.carouselItems = reduce(itemElements, (acc, itemEl) => {
        var width = this.getElementWidth(itemEl);
        var offsetLeft = 0;
        if (acc.length > 0) {
          offsetLeft = last(acc).offsetLeft + last(acc).width;
        }
        acc.push({
          angularEl: angular.element(itemEl),
          width: width,
          offsetLeft: offsetLeft
        });
        return acc;
      }, []);

      this.odCarouselContainer =
        angular.element(containerEl);
      this.odCarouselContainer.scrollLeft(0);

      this.throttledLoadImages();
    }

    // Lazy load the current item, and two items to the left and right.
    //
    // Uses the `scope().lazyLoad` pattern to delegate the loading to the
    // carousel item.
    //
    // Private - call `throttledLoadImages` instead to ensure good performance.
    _loadImages() {
      // We use $timeout() instead of $scope.$apply because this function will be
      // called as soon as the page starts to render, and will sometimes conflict
      // with a $digest that is already in progress.
      //
      // $timeout has the same effect as $scope.$apply, but runs in the next
      // cycle and therefore doesn't conflict with the initial $digest.
      $timeout(() => {
        // Load only one image until the carousel starts being used
        if (this.currentPosition === 0 && this.carouselItems.length >= 1) {
          this.tryLazyLoadingElement(this.carouselItems[0].angularEl);
          return;
        }

        var minIndex = Math.max(0, this.currentIndex - 2);
        var maxIndex = Math.min(this.currentIndex + 2,
                                this.carouselItems.length - 1);
        for (var i = minIndex; i <= maxIndex; i++) {
          this.tryLazyLoadingElement(this.carouselItems[i].angularEl);
        }
      });
    }

    tryLazyLoadingElement(angularEl) {
      if (angularEl.scope()) {
        angularEl.scope().lazyLoad = true;
      }
    }

    next(e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      this.targetPosition = this.targetPosition || this.currentPosition;
      this.targetPosition = Math.min(
        Math.floor(this.targetPosition) + 1,
        this.carouselItems.length - 1);

      this.scrollToIndex(this.targetPosition);
    }

    prev(e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      this.targetPosition = this.targetPosition || this.currentPosition;
      this.targetPosition = Math.max(Math.ceil(this.targetPosition) - 1, 0);

      this.scrollToIndex(this.targetPosition);
    }

    scrollToIndex(index, animateTransition = false) {
      if (this.odCarouselContainer) {
        // Different animation functions look good on different size screens.
        // On desktop, it's hard to watch a slow, full-screen-width slide
        // animation.
        let transitionFunction;
        let transitionDuration;
        // $mediaQuery('xs') should work here, but doesn't
        if (animateTransition || !$mediaQuery('gt-xs')) {
          // We use an easing function that starts the animation at full speed,
          // but eases at the end. This looks good compared to other animations
          // when you click next/prev many times in a row, and is consistent
          // with swipe UX on mobile.
          transitionFunction = (t) => t * (2 - t);
          transitionDuration = 500;
        } else {
          // No transition on desktop; it's just disorienting
          transitionFunction = (t) => t;
          transitionDuration = 0;
        }
        this.odCarouselContainer.scrollTo(
            this.carouselItems[index].offsetLeft,
            0,
            transitionDuration,
            transitionFunction)
        .then(() => {
          this.targetPosition = null;
        });
      }
    }

    // Update this.currentPosition and this.currentIndex
    updateCurrentPosition() {
      if (!this.odCarouselContainer) {
        return 0;
      }

      var scrollLeft = this.odCarouselContainer.scrollLeft();
      var currentPosition = findIndex(this.carouselItems, (item) => {
        return scrollLeft <= item.offsetLeft;
      });
      // this can happen on scrolling on the way to the end very quickly
      if (currentPosition === -1) {
        currentPosition = this.carouselItems.length - 1;
      }
      if (scrollLeft < this.carouselItems[currentPosition].offsetLeft) {
        currentPosition -= 0.5;
      }

      this.currentPosition = currentPosition;
      this.currentIndex = Math.floor(currentPosition);
    }

    getElementWidth(el) {
      return parseInt($window.getComputedStyle(el).width);
    }

    // Find child DOM elements that either have `name` as their tag name (e.g.
    // <child-element>) or as an attribute (e.g. <div child-element>).
    // Parallel to angular's `restrict: 'EA'`.
    getChildrenByElementOrAttribute(el, name) {
      return filter(el.children, (child) => {
        return child.tagName.toLowerCase() === name.toLowerCase() ||
          child.getAttribute(name) !== null;
      });
    }

    onScroll() {
      this.updateCurrentPosition();
      this.throttledLoadImages();
    }

    // Trigger this from the carousel-item every time a new item is added -
    // e.g. by ng-repeat.
    carouselItemAdded() {
      this.debouncedRebuild();
    }
  }

  return {
    restrict: 'EA',
    controller: OdCarousel,
    require: 'odCarousel',
    bindToController: true,
    controllerAs: 'carouselCtrl',
    scope: true
  };
})

.directive('odCarouselContainer', () => {
  return {
    restrict: 'EA',
    require: '^odCarousel',

    controller: class OdCarouselContainer {
      constructor($element) {
        const isScrollSnapSupported = 'scrollSnapType' in document.documentElement.style ||
                                      'webkitScrollSnapType' in document.documentElement.style;
        const isTouchSupported = 'ontouchmove' in window;

        // only activate for touch + no scroll snap
        if (!isTouchSupported || isScrollSnapSupported) {
          return;
        }

        // HACK: Hardcoded to full-width carousels, will need to be refactored
        // to use more generally.
        this.$element = $element;
        this.width = this.$element.prop('offsetWidth');
        this.x0 = this.x = this.$element.scrollLeft();
        this.t = null;
        this.v = 0;

        this.$element
          .css('overflow', 'hidden')
          .on('touchstart', this.onTouchStart.bind(this))
          .on('touchmove', this.onTouchMove.bind(this))
          .on('touchend', this.onTouchEnd.bind(this));
      }

      getCoordinates(e) {
        const touches = e.touches && e.touches.length ? e.touches : [e];
        e = (e.changedTouches && e.changedTouches[0]) ||
            (e.originalEvent && e.originalEvent.changedTouches &&
                e.originalEvent.changedTouches[0]) ||
            touches[0].originalEvent || touches[0];

        return { x: e.clientX, y: e.clientY };
      }

      onTouchStart(e) {
        this.x0 = this.$element.scrollLeft();
        this.touch0 = this.getCoordinates(e);
        this.children = this.$element.children();
      }

      onTouchMove(e) {
        // Bail out if movement is vertical
        const touch = this.getCoordinates(e);
        const dx = Math.abs(touch.x - this.touch0.x);
        const dy = Math.abs(touch.y - this.touch0.y);
        if (dy > dx) {
          return;
        }

        e.preventDefault();

        // "Scroll" content with hardware acceleration
        const x = this.touch0.x - touch.x;
        this.children.css('webkitTransform', `translate3d(${-x}px,0,0)`);

        // Track instantaneous velocity by watching position over time.
        const t = Date.now();
        const dt = t - this.t;

        // Too short/noisy of a sample duration; wait for another.
        if (dt < 8) {
          return;
        }

        this.v = (x - this.x) / dt;
        this.x = x;
        this.t = t;
      }

      // Take over scrolling to coast to a snap point based on velocity at the
      // end of the swipe
      onTouchEnd(e) {
        // Calculate snap position
        const x = this.x0 + this.touch0.x - this.getCoordinates(e).x;
        var xf = Math.round((x + this.v * 200) / this.width) * this.width;
        xf = this.constrain(xf, this.x0 - this.width, this.x0 + this.width);

        // Minimum speed is 1.
        if (Math.abs(this.v) < 1) {
          this.v = xf > x ? 1 : -1;
        }

        // Calculate duration of coast
        this.dt = (xf - x) / this.v;
        let a = (Math.abs(this.v) - 1) / 3;

        // Un-"scroll" the children and actually scroll the content
        this.children.css('webkitTransform', 'translate3d(0,0,0)');
        this.$element.scrollLeft(x);
        // Easing function: https://www.desmos.com/calculator/r270p2ri1m
        this.$element.scrollLeft(xf, this.dt,
            t => Math.min((a - 1) * (t - 1) * (t - 1) + 1, 1));
      }

      constrain(x, min, max) {
        return Math.min(Math.max(x, min), max);
      }
    },

    link(scope, elem, attrs, odCarousel) {
      elem.on('scroll', throttle(() => {
        odCarousel.onScroll();
      }, 10));
    }
  };
})

// Every time a new od-carousel-item is detected, we need to notify od-carousel
// so it can rebuild. This will happen e.g. if ng-repeat creates additional
// carousel items, but also if you create more carousel-items at any time!
.directive('odCarouselItem', () => {
  return {
    restrict: 'EA',
    require: '^odCarousel',
    link(scope, elem, attrs, odCarousel) {
      odCarousel.carouselItemAdded();
    },
    scope: true
  };
})
.name;



/** WEBPACK FOOTER **
 ** ./javascripts/common/components/od-carousel.js
 **/